import React, { useEffect, useState } from "react"
import { Container, Row, Col, Tabs, Tab, Badge } from "react-bootstrap"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useAuth } from "../components/auth/useAuth"
import { editPolicy, readPolicy } from "../actions/other/policiesActions"
import { getReceiptsByPolicy } from "../actions/other/receiptsActions"
import { getSinestersByPolicy } from "../actions/other/sinestersActions"
import { getCompanias } from "../actions/other/companiasActions"
import { RootState, AppDispatch } from "../store"
import { CustomDataTable } from "../components/design/Datatables/CustomDataTable"
import PolicyForm from "../components/forms/PolicyForm"
import Swal from "sweetalert2"
import Loader from "../components/loaders/Loader"
import { MoveRight } from "lucide-react"
import { PDFDownloadLink } from '@react-pdf/renderer'
import CartaAnulacionPDF from "../components/pdf/CartaAnulacionPDF"

const formatDate = (value?: string | null): string => {
    if (!value) return "—"
    const d = new Date(value)
    if (isNaN(d.getTime())) return "—"
    return new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

export interface Policy {
    _id?: string
    id?: string
    numero?: number
    nifCliente?: string
    nombreCliente?: string
    nombreCompania?: string
    fechaEfecto?: string
    fechaEfectoCartera?: string
    fechaLimiteTemporal?: string
    fechaAnulacion?: string
    idEstado?: string
    idCliente?: string
    riesgo?: string
    subramo?: string
    descripcionSubramo?: string
    formaPago?: string
    arrayBanks?: string[]
    idMedioCobro?: string
}
export interface Client {
    _id?: string
    id?: string
}
export interface Receipt {
    _id?: string
    id?: string
    idCliente?: string
    idPoliza?: string
    tipo?: string
    clase?: string
    nifCliente?: string
    ordinal?: string
    numero?: string
    fechaEfecto?: string
    estado?: string
    primaTotal?: number
    comisionCorreduria?: string
    nombreCompletoTomador?: string
    medioCobro?: string
    hsp?: number
}
export interface Sinester {
    _id?: string
    id?: string
    refCia?: string
    abrevCia?: string
    tomador?: string
    compania?: string
    fecha?: string
    fechaApertura?: string
    fechaCierre?: string
    fechaEfecto?: string
    estado?: string
    descripcion?: string
    descripcionProducto?: string
    arrayNotes?: any[]
}

export default function PolicyDetail() {
    const { t } = useTranslation()
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch: AppDispatch = useDispatch()
    const { clientauth } = useAuth()

    const { policy, resultpolicy, loadingdata } = useSelector((state: RootState) => state.policies)
    const { receipts, loading: loadingReceipts } = useSelector((state: RootState) => state.receipts)
    const { sinesters, loading: loadingSinesters } = useSelector((state: RootState) => state.sinesters)


    const [isEditing, setIsEditing] = useState(false)
    const [policySelected, setPolicySelected] = useState<Policy | null>(null)

    useEffect(() => {
        if (id) {
            dispatch(readPolicy(id) as any)
            dispatch(getReceiptsByPolicy(id) as any)
            dispatch(getSinestersByPolicy(id) as any)
        }
    }, [id, dispatch])

    useEffect(() => {
        if (clientauth) {
            dispatch(getCompanias() as any)
        }
    }, [dispatch, t, clientauth])

    useEffect(() => {
        if (policy) {
            setPolicySelected(policy)
        }
    }, [policy])

    const handleInputChange = (e: any) => {
        const { name, value } = e.target
        setPolicySelected(prev => (prev ? { ...prev, [name]: value } : null))
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsEditing(true)
        // El detalle de póliza llega con _id (no con id), a diferencia de
        // clientes y siniestros. Si se envía undefined, la API guarda la nota
        // bajo el identificador "undefined" y el comentario desaparece.
        const policyId = policySelected?.id ?? policySelected?._id ?? id
        await dispatch(editPolicy(policySelected, policyId) as any)
        // Fallback: si la API devuelve error el reducer no setea resultpolicy
        // y el useEffect de editsuccess no se dispara — forzamos el reset aquí
        setIsEditing(false)
    }

    useEffect(() => {
        if (!resultpolicy) return

        dispatch({ type: "POLICY_RESET_RESULT" })
        if (resultpolicy === "editsuccess") {
            // Recargar para obtener _id/ebrokerId asignados por el backend.
            // Sin esto la nota sigue pareciendo editable y se puede reenviar a ebroker.
            if (id) {
                dispatch(readPolicy(id) as any)
            }
            Swal.fire({
                icon: "success",
                confirmButtonText: t("global.great"),
                text: t("policyTranslate.editSuccess"),
            }).then(() => {
                setIsEditing(false)
            })
        }
    }, [resultpolicy, dispatch, t, id])


    const pathParts = location.pathname.split("/")
    const base = pathParts[1]

    const selectReceipt = (receipt: Receipt) => {
        navigate(`/${base}/recibos/${receipt.id}`)
    }
    const selectSinester = (sinester: Sinester) => {
        navigate(`/${base}/siniestros/${sinester.id}`)
    }

    const getBadgeStyle = (estado: string) => {
        switch (estado) {
            case "A":
                return { bg: "danger" };
            case "V":
                return { bg: "success" };
            case "S":
                return { className: "bg-orange" };
            default:
                return { bg: "secondary" };
        }
    };

    const style = getBadgeStyle(policy?.idEstado);

    const columnsReceipt = [
        {
            name: t("receiptTranslate.numero"),
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {receipt?.numero || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.numero || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.nombreCompletoTomador"),
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {receipt?.nombreCompletoTomador || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.nombreCompletoTomador || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.fechaEfecto"),
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {formatDate(receipt?.fechaEfecto)}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.fechaEfecto || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.estado"),
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {receipt?.estado || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.estado || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.importeTotal"),
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {((receipt?.primaTotal ?? 0) + (receipt?.hsp ?? 0)).toFixed(2) + " €" || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.primaTotal || "—",
            sortable: true,
        }

    ]

    const columnsSinester = [
        {
            name: t("sinesterTranslate.tomador"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {sinester?.tomador || "—"}
                </div>
            ),
            selector: (sinester: Sinester) => sinester?.tomador || "—",
            sortable: true,
        }, {
            name: t("sinesterTranslate.compania"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {sinester?.abrevCia || "—"}
                </div>
            ),
            selector: (sinester: Sinester) => sinester?.abrevCia || "—",
            sortable: true,
        }, {
            name: t("sinesterTranslate.refCia"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {sinester?.refCia || "—"}
                </div>
            ),
            selector: (sinester: Sinester) => sinester?.refCia || "—",
            sortable: true,
        }, {
            name: t("sinesterTranslate.descripcionProducto"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {sinester?.descripcionProducto || "—"}
                </div>
            ),
            selector: (sinester: Sinester) => sinester?.descripcionProducto || "—",
            sortable: true,
        }, {
            name: t("sinesterTranslate.descripcion"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {sinester?.descripcion || "—"}
                </div>
            ),
            selector: (sinester: Sinester) => sinester?.descripcion || "—",
            sortable: true,
        }, {
            name: t("sinesterTranslate.estado"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {sinester?.estado || "—"}
                </div>
            )
        }, {
            name: t("sinesterTranslate.fecha"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {formatDate(sinester?.fecha)}
                </div>
            )
        }, {
            name: t("sinesterTranslate.fechaApertura"),
            cell: (sinester: Sinester) => (
                <div className="cursor text-start" onClick={() => selectSinester(sinester)}>
                    {formatDate(sinester?.fechaApertura)}
                </div>
            )
        }
    ]

    return (
        <Container fluid className='p-1 py-5 p-md-5'>
            <Row>
                <Col>
                    <h1>{t('policyTranslate.policyDetails')}</h1>
                </Col>

            </Row>
            <Row className="my-3">
                <Col>
                    <Tabs
                        defaultActiveKey="general"
                        id="client-tabs"
                        className="mb-0"
                        fill
                    >
                        <Tab eventKey="general" title={t('policyTranslate.policy')}>
                            <div className="border border-top-0 rounded-bottom p-4 bg-white">
                                {loadingdata
                                    ?
                                    <Loader />
                                    :
                                    <Row className="my-1">
                                        <Col xs={12} className="mb-3 d-flex align-items-center justify-content-between gap-3">
                                            <Badge {...style}>{
                                                policy?.idEstado === "A" ?
                                                    t("policyTranslate.anulado") :
                                                    policy?.idEstado === "V" ?
                                                        t("policyTranslate.enVigor") :
                                                        policy?.idEstado === "S" ?
                                                            t("policyTranslate.suspension") :
                                                            "—"
                                            }</Badge>
                                            <Link to={`/${base}/clientes/${policy?.idCliente}`} className="btn btn-secondary">
                                                {t("sinesterTranslate.viewClient")} <MoveRight size={16} />
                                            </Link>
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>
                                                {t("policyTranslate.numPoliza")}
                                            </h5>
                                            <hr />
                                            {policy?.numero || "—"}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("policyTranslate.descripcionSubramo")}</h5>
                                            <hr />
                                            {policy?.nombreCompania && policy?.descripcionSubramo
                                                ? `${policy.nombreCompania} - ${policy.descripcionSubramo}`
                                                : policy?.descripcionSubramo || policy?.nombreCompania || "—"}
                                        </Col>
                                        <Col xs={12} lg={6} className="mb-3">
                                            <h5>{t("policyTranslate.nombreCliente")}</h5>
                                            <hr />
                                            <Link to={`/${base}/clientes/${policy?.idCliente}`} className="link">
                                                {policy?.nombreCliente || "—"}
                                            </Link>
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("policyTranslate.fechaEfecto")}</h5>
                                            <hr />
                                            {formatDate(policy?.fechaEfecto)}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("policyTranslate.fechaEfectoCartera")}</h5>
                                            <hr />
                                            {formatDate(policy?.fechaEfectoCartera)}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("policyTranslate.fechaLimiteTemporal")}</h5>
                                            <hr />
                                            {formatDate(policy?.fechaLimiteTemporal)}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("policyTranslate.fechaAnulacion")}</h5>
                                            <hr />
                                            {formatDate(policy?.fechaAnulacion)}
                                        </Col>
                                        <Col xs={12} lg={4} className="mb-3">
                                            <h5>{t("policyTranslate.formaPago")}</h5>
                                            <hr />
                                            {policy?.formaPago || "—"}
                                        </Col>
                                        <Col xs={12} lg={8} className="mb-3">
                                            <h5>{t("policyTranslate.cuentaBancaria")}</h5>
                                            <hr />
                                            {(policy?.arrayBanks ?? []).join(", ") || "—"}
                                        </Col>
                                        <Col xs={12} className="mb-3">
                                            <h5>{t("policyTranslate.letterOfCancellation")}</h5>
                                            <hr />
                                            {policy && (
                                                <PDFDownloadLink
                                                    document={<CartaAnulacionPDF policy={policy} />}
                                                    fileName={`carta-anulacion-poliza-${policy.numero || 'sin-numero'}.pdf`}
                                                    className="btn btn-outline-secondary btn-sm"
                                                >
                                                    {({ loading }) =>
                                                        loading ? t("global.loading") + "..." : t("global.download")
                                                    }
                                                </PDFDownloadLink>
                                            )}
                                        </Col>
                                    </Row>
                                }
                            </div>
                        </Tab>
                        <Tab eventKey="comentarios" title={t('policyTranslate.comments')}>
                            <div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
                                <PolicyForm
                                    state={policySelected}
                                    handleInputChange={handleInputChange}
                                    submit={handleSubmit}
                                    isEditing={isEditing}
                                    loadingdata={loadingdata}
                                />
                            </div>
                        </Tab>
                        <Tab eventKey="siniestros" title={t('policyTranslate.sinesters')}>
                            <div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
                                {loadingSinesters ? (
                                    <Loader />
                                ) : sinesters?.length ? (
                                    <CustomDataTable
                                        columns={columnsSinester}
                                        data={sinesters}
                                        pagination
                                    />
                                ) : (
                                    <h4>{t("sinesterTranslate.thereisnotsinester")}</h4>
                                )}
                            </div>
                        </Tab>
                        <Tab eventKey="recibos" title={t('policyTranslate.receipts')}>
                            <div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
                                {loadingReceipts ? (
                                    <Loader />
                                ) : receipts?.length > 0 ? (
                                    <CustomDataTable
                                        columns={columnsReceipt}
                                        data={receipts}
                                        pagination
                                    />
                                ) : (
                                    <h4 className="mt-4">{t("receiptTranslate.thereisnotreceipt")}</h4>
                                )}
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Container>
    )
}